import apiClient from './api';
import { validateRequiredParams } from '@/utils/apiUtils';
import type {
  CollaborationCreator,
  CollaborationCreatorPost,
  CollaborationCreatorPostsParams,
  CollaborationCreatorPostsResponse,
  CollaborationCreatorsParams,
  CollaborationCreatorsResponse,
} from '@/types/collaborationCreators.types';

const asNumber = (value: unknown, fallback = 0): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const asNullableString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

// Large warehouse ids (creator_account_id, post_id) arrive as strings and MUST
// stay strings: they exceed 2^53 and Number()/JSON parsing would round them,
// breaking the drill-down lookup. Accept a string or number, emit a trimmed
// non-empty string, or '' when absent.
const asId = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
};

const normalizeCreator = (raw: unknown): CollaborationCreator | null => {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;

  const accountGroupCode =
    typeof row.account_group_code === 'string' ? row.account_group_code.trim() : '';
  const accountGroupName =
    typeof row.account_group_name === 'string' ? row.account_group_name.trim() : '';
  const creatorAccountId = asId(row.creator_account_id);

  if (!accountGroupCode || !creatorAccountId) return null;

  return {
    account_group_code: accountGroupCode,
    account_group_name: accountGroupName || accountGroupCode,
    creator_account_id: creatorAccountId,
    creator_username: asNullableString(row.creator_username),
    creator_name: asNullableString(row.creator_name),
    creator_verified: row.creator_verified === true,
    collab_posts: asNumber(row.collab_posts),
    total_engagement: asNumber(row.total_engagement),
    avg_engagement_per_post: asNumber(row.avg_engagement_per_post),
    total_likes: asNumber(row.total_likes),
    total_comments: asNumber(row.total_comments),
    total_views: asNumber(row.total_views),
    platforms: asNullableString(row.platforms),
    first_collab_at: asNullableString(row.first_collab_at),
    last_collab_at: asNullableString(row.last_collab_at),
    collabs_per_month: asNumber(row.collabs_per_month),
    brands_with_creator_in_tenant: asNumber(row.brands_with_creator_in_tenant),
  };
};

export const getCollaborationCreators = async (
  params: CollaborationCreatorsParams
): Promise<CollaborationCreatorsResponse> => {
  validateRequiredParams(params, ['tenant_code']);

  const { tenant_code, account_group_codes, shared_only, start_date, end_date } = params;
  const queryParams: Record<string, unknown> = {};

  if (account_group_codes?.length) {
    queryParams.account_group_codes = account_group_codes;
  }

  if (shared_only === true) {
    queryParams.shared_only = true;
  }

  if (start_date && end_date) {
    queryParams.start_date = start_date;
    queryParams.end_date = end_date;
  }

  const response = await apiClient.get<unknown>(
    `/analytics/${encodeURIComponent(tenant_code)}/collaboration-creators`,
    { params: queryParams }
  );

  const body = response.data;
  if (!Array.isArray(body)) return [];

  return body.map(normalizeCreator).filter((row): row is CollaborationCreator => row !== null);
};

const normalizeCreatorPost = (raw: unknown): CollaborationCreatorPost | null => {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;

  const postId = asId(row.post_id);
  if (!postId) return null;

  return {
    post_id: postId,
    post_url: asNullableString(row.post_url),
    account_group_code:
      typeof row.account_group_code === 'string' ? row.account_group_code.trim() : '',
    account_group_name:
      typeof row.account_group_name === 'string' ? row.account_group_name.trim() : '',
    platform_name: asNullableString(row.platform_name),
    published_at: asNullableString(row.published_at),
    likes: asNumber(row.likes),
    comments: asNumber(row.comments),
    views: asNumber(row.views),
    engagement: asNumber(row.engagement),
  };
};

export const getCollaborationCreatorPosts = async (
  params: CollaborationCreatorPostsParams
): Promise<CollaborationCreatorPostsResponse> => {
  validateRequiredParams(params, ['tenant_code', 'creator_account_id']);

  const { tenant_code, creator_account_id, account_group_codes, start_date, end_date } = params;
  const queryParams: Record<string, unknown> = {};

  if (account_group_codes?.length) {
    queryParams.account_group_codes = account_group_codes;
  }

  if (start_date && end_date) {
    queryParams.start_date = start_date;
    queryParams.end_date = end_date;
  }

  const response = await apiClient.get<unknown>(
    `/analytics/${encodeURIComponent(tenant_code)}/collaboration-creators/` +
      `${encodeURIComponent(String(creator_account_id))}/posts`,
    { params: queryParams }
  );

  const body = response.data;
  if (!Array.isArray(body)) return [];

  return body
    .map(normalizeCreatorPost)
    .filter((row): row is CollaborationCreatorPost => row !== null);
};
